const DAY = 86400;

/** Seconds since midnight to a clock face. Values past midnight wrap. */
export function hhmm(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '--:--';
  const wrapped = ((seconds % DAY) + DAY) % DAY;
  const h = Math.floor(wrapped / 3600);
  const m = Math.floor((wrapped % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 7:43 am from seconds since midnight: the way a dispatcher says a time. */
export function ampm(s: number): string {
  const total = Math.floor(s / 60);
  const h24 = Math.floor(total / 60) % 24;
  const m = total % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${h24 < 12 ? 'am' : 'pm'}`;
}

/** Drift against plan, in the terms a dispatcher speaks: "+12m", "-4m", "on time". */
export function delta(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '';
  const minutes = Math.round(seconds / 60);
  if (minutes === 0) return 'on time';
  return minutes > 0 ? `+${minutes}m` : `${minutes}m`;
}

export type Tone = 'ok' | 'warn' | 'late' | 'idle';

/**
 * How worried to be about a drift.
 *
 * Under five minutes is noise on a service route and colouring it would train
 * dispatchers to ignore the colour. Fifteen minutes is where a customer starts
 * noticing, so that is where it turns red.
 */
export function driftTone(seconds: number | null | undefined): Tone {
  if (seconds === null || seconds === undefined) return 'idle';
  if (seconds >= 900) return 'late';
  if (seconds >= 300) return 'warn';
  return 'ok';
}

export type DistanceUnit = 'mi' | 'km';

/**
 * A distance in the territory's own unit.
 *
 * This was three different renderings across the console -- a km helper, an
 * inline division by 1609 on the map panel, and a miles helper in simulations
 * -- so the same route could read 87 mi in one place and 140 km in another on
 * the same screen. One decimal, as the incumbent shows it.
 */
/** Metres into the unit an operator types in, and back. */
export function fromMeters(meters: number, unit: DistanceUnit): number {
  return unit === 'km' ? meters / 1000 : meters * 0.000621371;
}

export function toMeters(value: number, unit: DistanceUnit): number {
  return Math.round(unit === 'km' ? value * 1000 : value / 0.000621371);
}

/** Seconds as hours, to a quarter, for a limit an operator sets in hours. */
export function hoursOf(seconds: number | null | undefined): number {
  if (seconds === null || seconds === undefined) return 0;
  return Math.round((seconds / 3600) * 100) / 100;
}

export function distance(meters: number, unit: DistanceUnit = 'mi'): string {
  return unit === 'km'
    ? `${(meters / 1000).toFixed(1)} km`
    : `${(meters * 0.000621371).toFixed(1)} mi`;
}

export function hours(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Money, in the territory's own currency.
 *
 * The incumbent prints `2627.2 USD`. We render it through Intl instead, which
 * gets grouping and the symbol's position right in every locale, and falls
 * back to their plain form if the code is one Intl does not know.
 */
export function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toFixed(1)} ${currency}`;
  }
}

/** Long spans the incumbent's way: `2 d 14 h 46 min`. */
export function longHours(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return [d > 0 ? `${d} d` : null, d > 0 || h > 0 ? `${h} h` : null, `${m} min`]
    .filter(Boolean)
    .join(' ');
}

export function addressLine(
  address: { line1?: string; city?: string; region?: string } | null,
): string {
  if (!address) return '';
  return [address.line1, address.city].filter(Boolean).join(', ');
}

/**
 * A date as this operator reads one: `10-19-2026`.
 *
 * Display only. Every date that is a *value* -- a URL segment, a date input,
 * anything on its way to the API -- stays ISO, because that is the one format
 * that sorts correctly and that the server parses. This is the other end: a
 * dispatcher scanning a strip of calendar exceptions should not have to work
 * out whether `2026-09-30` is the thirtieth of September or the ninth of
 * October.
 *
 * Takes whatever the API hands over -- a Date, an ISO string, a timestamp --
 * and reads only the date part, never converting zones. A plan's date is a
 * day in the territory, not an instant, and `new Date('2026-10-19')` parsed as
 * UTC and rendered in the browser's zone is the eighteenth for half the world.
 */
export function shortDate(value: unknown): string {
  if (!value) return '';
  const iso = value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[2]}-${m[3]}-${m[1]}` : iso;
}

/** Today in a given IANA zone, as YYYY-MM-DD. */
export function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Wall-clock seconds since midnight in a given zone, for any instant. */
export function secondsIn(timeZone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return (get('hour') % 24) * 3600 + get('minute') * 60 + get('second');
}

/** Current wall-clock seconds since midnight in a given zone. */
export function nowSecondsIn(timeZone: string): number {
  return secondsIn(timeZone, new Date());
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Not started',
  en_route: 'On the way',
  on_site: 'On site',
  completed: 'Completed',
  failed: 'Could not complete',
  skipped: 'Skipped',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/**
 * What to call a stop: `8.3`.
 *
 * The stored label wins. It was minted when the plan was built and travels with
 * the stop through every manual move, so a job dragged above its neighbour --
 * or onto a different technician -- keeps the name the dispatcher just used to
 * talk about it. Falling back to the position covers plans built before labels
 * were stored, and reads the same as it always did.
 */
export function stopLabel(
  stop: { label?: string | null; sequence: number },
  vehicleIdx: number | null,
): string {
  if (stop.label) return stop.label;
  return vehicleIdx === null ? String(stop.sequence) : `${vehicleIdx}.${stop.sequence}`;
}
